'use strict';

/**
 * Pure taxonomy/tree helpers for the avoidances selector.
 *
 * Extracted verbatim from AvoidanceSelect.react.js (EAT-3929) with no behavior
 * change so the component can be decomposed and these algorithms unit-tested in
 * isolation. Every function here is pure except where noted (assignOriginalIndex
 * and markHasChildren mutate their argument, matching the original in-component
 * behavior).
 */

/** Keep only taxonomy nodes whose category_key is in ancestorKeysSet, recursively. */
export function filterTaxonomy(taxonomy, ancestorKeysSet) {
    return taxonomy.reduce((acc, item) => {
        if (ancestorKeysSet.has(item.category_key)) {
            const filteredChildren = filterTaxonomy((item.children || []), ancestorKeysSet);
            acc.push({ ...item, children: filteredChildren });
        }
        return acc;
    }, []);
}

/**
 * Flatten a filtered taxonomy into an indented list, attaching each matching
 * element (by ancestor_keys) beneath its leaf category.
 */
export function combineTaxonomyAndElements(filteredTaxonomy, responseElements) {
    const combinedArray = [];

    function traverse(taxonomyNode, currentIndent) {
        combinedArray.push({ ...taxonomyNode, indents: currentIndent });

        if (taxonomyNode.children.length === 0) {
            const matchingElements = responseElements.filter(element =>
                (element.ancestor_keys || []).includes(taxonomyNode.category_key)
            );
            matchingElements.forEach(element => {
                combinedArray.push({ ...element, indents: currentIndent + 1 });
            });
        } else {
            taxonomyNode.children.forEach(child => traverse(child, currentIndent + 1));
        }
    }

    filteredTaxonomy.forEach(node => traverse(node, 0));

    return combinedArray;
}

/** Split a flattened list into groups, each starting at an indent-0 row. */
export function groupCombinedArray(sortedArray) {
    const groupedArray = [];
    let currentGroup = [];

    sortedArray.forEach(item => {
        if (item.indents === 0) {
            if (currentGroup.length > 0) {
                groupedArray.push(currentGroup);
            }
            currentGroup = [item];
        } else {
            currentGroup.push(item);
        }
    });

    if (currentGroup.length > 0) {
        groupedArray.push(currentGroup);
    }

    return groupedArray;
}

/** Order groups by the minimum originalIndex of any item within each group. */
export function sortGroupedArray(groupedArray) {
    const groupMinIndexes = groupedArray.map(group => {
        const minIndex = group.reduce((min, item) => {
            if ('originalIndex' in item) {
                return Math.min(min, item.originalIndex);
            }
            return min;
        }, Infinity);
        return { group, minIndex };
    });

    groupMinIndexes.sort((a, b) => a.minIndex - b.minIndex);

    return groupMinIndexes.map(item => item.group);
}

/** Stamp category nodes with their originalIndex from the elements array (mutates category). */
export function assignOriginalIndex(category, elements) {
    const originalIndex = elements.findIndex(element => element.category_key === category.category_key);
    if (originalIndex !== -1) {
        category.originalIndex = originalIndex;
    }

    if (category.children && category.children.length > 0) {
        category.children.forEach(child => {
            assignOriginalIndex(child, elements);
        });
    }
}

/** True if categoryKey is parentKeys or any of their ancestors (looked up in avoidResults). */
export function isDescendantOf(parentKeys, categoryKey, avoidResults) {
    if (!parentKeys?.length) {
        return false;
    }

    if (parentKeys.includes(categoryKey)) {
        return true;
    }

    const grandParentKeys = parentKeys.flatMap(parentKey => {
        const result = Object.values(avoidResults).find(item => item.category_key === parentKey);

        if (!result || !result.ancestor_keys) {
            return [];
        }

        return result.ancestor_keys;
    });

    return isDescendantOf(grandParentKeys, categoryKey, avoidResults);
}

/**
 * EAT-4017: flag each category option with whether it has any child in the
 * current (search-filtered) option set. Only flagged rows should render an
 * expand chevron; otherwise tapping a childless row is a dead no-op. Mutates
 * and returns the options array.
 */
export function markHasChildren(avoidOptions) {
    const parentKeysWithChildren = new Set(avoidOptions.flatMap(option => option.parents || []));

    avoidOptions.forEach(option => {
        option.hasChildren = !!option.categoryKey && parentKeysWithChildren.has(option.categoryKey);
    });

    return avoidOptions;
}
